/**
 * Coarse deployment tier, derived from the fine-grained `NG_APP_ENVIRONMENT`
 * label.
 *
 * Every pipeline names its environment differently — Cloud Build ships `dev`,
 * the web deploys ship `prod-eu` / `next-au` / `canary` / an RC name, the Tauri
 * workflows ship `production-eu`. That precision is useful for narrowing to a
 * single deploy, and useless as the answer to "is this real usage?", which is
 * the filter a UX or product question actually starts from.
 *
 * `environment_tier` is registered as a super-property alongside `environment`,
 * so a dashboard filters `environment_tier = production` once and keeps working
 * when a new region or release channel appears.
 */
/**
 * Tier names deliberately reuse the vocabulary already in `environment.ts` and
 * the docs (`local` / `dev` / `staging` / `production`) rather than inventing a
 * parallel one.
 */
export type EnvironmentTier =
  | 'production'
  | 'pre-production'
  | 'dev'
  | 'local'
  | 'unknown';

/**
 * Map an environment label to its tier. Matches on a prefix so region and
 * channel suffixes (`prod-eu`, `production-au`, `next-us`, `rc-1.2.3`) need no
 * per-value maintenance.
 */
export function environmentTier(
  environment: string | undefined,
): EnvironmentTier {
  const value = (environment ?? '').trim().toLowerCase();
  if (!value) return 'unknown';
  if (value.startsWith('prod')) return 'production';
  if (
    value.startsWith('next') ||
    value.startsWith('canary') ||
    value.startsWith('rc') ||
    value.startsWith('staging') ||
    value.startsWith('uat')
  ) {
    return 'pre-production';
  }
  if (value.startsWith('dev') || value.startsWith('test')) return 'dev';
  if (value.startsWith('local')) return 'local';
  return 'unknown';
}
